import CoreAPIService from '../../../utils/core-api';
import { fetchAction } from '../../actions';
import {
    BillingStep,
    BillSimple,
    ProductTypeExceptional,
    RectificationAddPayload,
    SaveExceptionalPayload,
} from '../api-types';
export type { ProductTypeExceptional };

export const name: string = 'bills';

export const types = {
    SIMPLE_BILLS_UNRECOVERABLE: name + '/SIMPLE_BILLS_UNRECOVERABLE',
    SIMPLE_BILLS_RECOVER: name + '/SIMPLE_BILLS_RECOVER',
    SIMPLE_RETRY_PAIEMENT: name + '/SIMPLE_RETRY_PAIEMENT',
    GROUPED_BILLS_EXPORT_SEPA: name + '/GROUPED_BILLS_EXPORT_SEPA',
    GROUPED_BILLS_EXPORT_ACCOUNTING: name + '/GROUPED_BILLS_EXPORT_ACCOUNTING',
    GROUPED_BILLS_PAIEMENT: name + '/GROUPED_BILLS_PAIEMENT',
    GROUPED_BILLS_PENDING_BULK_ACTION: name + '/GROUPED_BILLS_PENDING_BULK_ACTION',
    GROUPED_BILLS_PENDING_UPDATE_STATUS: name + '/GROUPED_BILLS_PENDING_UPDATE_STATUS',
    GROUPED_BILLS_PENDING_STEP: name + '/GROUPED_BILLS_PENDING_STEP',
    CREATE_BILLS_EXCEPTIONAL: name + '/CREATE_BILLS_EXCEPTIONAL',
    PRODUCT_TYPES_BILLS_EXCEPTIONAL: name + '/PRODUCT_TYPES_BILLS_EXCEPTIONAL',
    CREATE_BILLS_CREDIT: name + '/CREATE_BILLS_CREDIT',
    CREATE_BILLS_RECTIFICATION: name + '/CREATE_BILLS_RECTIFICATION',
};

export const groupedBillBulkActions = {
    PAY: 'pay',
    SEND: 'send',
    SEND_AND_PAY: 'sendAndPay',
};

export const groupedBillStatus = {
    PENDING: 'pending',
    PAYMENT_PENDING: 'payment_pending',
    PAYMENT_FAIL_SOFT: 'payment_fail_soft',
    PAYMENT_FAIL_HARD: 'payment_fail_hard',
    PAYMENT_MANUAL: 'payment_manual',
    PAID: 'paid',
    CANCELED: 'cancelled',
};

interface BillsState {
    bills_simple_list: BillSimple;
    product_types_exceptional: {
        data: ProductTypeExceptional[];
    };
    step: BillingStep;
}

const initialState: BillsState = {
    bills_simple_list: {} as BillSimple,
    product_types_exceptional: {
        data: [],
    },
    step: {} as BillingStep,
};

function updateGroupedStatusAction(id, status) {
    return CoreAPIService.put(`/billing/customerBills/${id}`, { status: status });
}

// reducer
const reducer = (state = initialState, action): BillsState => {
    const payload = action.payload;
    switch (action.type) {
        case types.SIMPLE_BILLS_UNRECOVERABLE:
        case types.SIMPLE_BILLS_RECOVER:
            return {
                ...state,
                bills_simple_list: payload,
            };
        case types.PRODUCT_TYPES_BILLS_EXCEPTIONAL:
            return {
                ...state,
                product_types_exceptional: payload,
            };
        case types.GROUPED_BILLS_PENDING_STEP:
            return {
                ...state,
                step: payload.data,
            };
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    setUnrecoverable: (id, callback?) =>
        fetchAction(
            CoreAPIService.patch(`/core/simple_bills/${id}/unrecoverable`, {}),
            types.SIMPLE_BILLS_UNRECOVERABLE,
            { id },
            { success: callback },
        ),
    reloadBill: (id, params?, callback?) =>
        fetchAction(
            CoreAPIService.patch(`/core/simple_bills/${id}/recover`, {}),
            types.SIMPLE_BILLS_RECOVER,
            Object.assign({ id, bill_id: id }, params),
            { success: callback },
        ),
    retryPaiement: (id, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/${id}/pay`, {}),
            types.SIMPLE_RETRY_PAIEMENT,
            { id },
            { success: callback },
        ),
    setPaiement: (id, flag: string, amount: number, callback?) =>
        fetchAction(
            CoreAPIService.put(`/billing/customerBills/${id}`, {
                status: flag,
                remaining_balance: amount,
            }),
            types.GROUPED_BILLS_PAIEMENT,
            { id },
            { success: callback },
        ),
    exportPaiement: (payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/month/export/accounting`, payload),
            types.GROUPED_BILLS_EXPORT_ACCOUNTING,
            {},
            { success: callback },
        ),
    exportSepa: (payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/month/export/sepa`, payload),
            types.GROUPED_BILLS_EXPORT_SEPA,
            {},
            { success: callback },
        ),
    saveExceptional: (payload: SaveExceptionalPayload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/exceptional`, payload),
            types.CREATE_BILLS_EXCEPTIONAL,
            {},
            { success: callback },
        ),
    getExceptionalProductTypes: (callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/productTypes/search`, {
                filters: { is_exceptional: 1 },
            }),
            types.PRODUCT_TYPES_BILLS_EXCEPTIONAL,
            {},
            { success: callback },
        ),
    saveCredit: (payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/adjustments`, payload),
            types.CREATE_BILLS_CREDIT,
            {},
            { success: callback },
        ),
    saveRectification: (id: string, payload: RectificationAddPayload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/${id}/correction`, payload),
            types.CREATE_BILLS_RECTIFICATION,
            {},
            { success: callback },
        ),
    getStep: (payload: { start_date: string; end_date: string }, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/month/step`, payload),
            types.GROUPED_BILLS_PENDING_STEP,
            {},
            { success: callback },
        ),
    bulkAction: (mode: string, payload: { start_date: string; end_date: string }, callback?) =>
        fetchAction(
            CoreAPIService.post(`/billing/customerBills/month/${mode}`, payload),
            types.GROUPED_BILLS_PENDING_BULK_ACTION,
            {},
            { success: callback },
        ),
    updateGroupedBillStatus: (id, status, callback?) => {
        return fetchAction(
            updateGroupedStatusAction(id, status),
            types.GROUPED_BILLS_PENDING_UPDATE_STATUS,
            { id },
            {
                success: callback,
            },
        );
    },
};

const localState = (state: any): BillsState => state[name] || initialState;
export const selectors = {
    getStep: (state) => localState(state).step,
    getBillsSimpleList: (state) => localState(state).bills_simple_list,
    getProductTypesExceptional: (state) =>
        localState(state).product_types_exceptional?.data as ProductTypeExceptional[],
};
